import React, { useState } from "react";
import {
  X,
  Building,
  User,
  Mail,
  Lock,
  Users,
  CheckCircle,
} from "lucide-react";
import { realAPI } from "../../services/realAPI";

interface SignupFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSignupComplete: (data: SignupData) => void;
  onSwitchToLogin: () => void;
}

export interface SignupData {
  organization: {
    name: string;
    industry: string;
    size: string;
    website?: string;
  };
  user: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    jobTitle: string;
    role: "ADMIN" | "MEMBER";
  };
  plan: "free" | "pro" | "enterprise";
}

const SignupForm: React.FC<SignupFormProps> = ({
  isOpen,
  onClose,
  onSignupComplete,
  onSwitchToLogin,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<SignupData>({
    organization: {
      name: "",
      industry: "",
      size: "",
      website: "",
    },
    user: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      jobTitle: "",
      role: "ADMIN",
    },
    plan: "free",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const industries = [
    "Technology",
    "Healthcare",
    "Finance",
    "Education",
    "Manufacturing",
    "Retail",
    "Consulting",
    "Marketing",
    "Non-profit",
    "Other",
  ];

  const companySizes = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "500+ employees",
  ];

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepNumber === 1) {
      if (!formData.organization.name.trim()) {
        newErrors.organizationName = "Organization name is required";
      }
      if (!formData.organization.industry) {
        newErrors.industry = "Please select an industry";
      }
      if (!formData.organization.size) {
        newErrors.size = "Please select organization size";
      }
    }

    if (stepNumber === 2) {
      if (!formData.user.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }
      if (!formData.user.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }
      if (!formData.user.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.user.email)) {
        newErrors.email = "Please enter a valid email";
      }
      if (!formData.user.password) {
        newErrors.password = "Password is required";
      } else if (formData.user.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }
      if (!formData.user.jobTitle.trim()) {
        newErrors.jobTitle = "Job title is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const resetForm = () => {
    setFormData({
      organization: {
        name: "",
        industry: "",
        size: "",
        website: "",
      },
      user: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        jobTitle: "",
        role: "ADMIN",
      },
      plan: "free",
    });
    setErrors({});
    setStep(1);
    setIsSuccess(false);
    setUserEmail("");
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setIsLoading(true);

    try {
      // Call mock API to create organization
      const result = await realAPI.createOrganization(formData);

      console.log("Organization created successfully:", result);

      setIsLoading(false);
      setIsSuccess(true);
      setUserEmail(formData.user.email);
      // Don't call onSignupComplete here as we're showing our own success message
    } catch (error) {
      console.error("Signup error:", error);
      setIsLoading(false);

      // Handle specific errors
      if (error instanceof Error) {
        if (error.message === "Email already exists") {
          setErrors({ email: "This email is already registered" });
        } else {
          setErrors({ general: "Something went wrong. Please try again." });
        }
      }
    }
  };

  const updateOrganization = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      organization: { ...prev.organization, [field]: value },
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const updateUser = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      user: { ...prev.user, [field]: value },
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isSuccess
                ? "Account Created!"
                : step === 1
                ? "Create Organization"
                : "Admin Account Setup"}
            </h2>
            {!isSuccess && (
              <p className="text-sm text-gray-600 mt-1">Step {step} of 2</p>
            )}
          </div>
          {!isSuccess && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Account created successfully!
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Please click on the verification email sent to{" "}
                <span className="font-medium">{userEmail}</span> to complete
                your registration.
              </p>
              <div className="mt-6">
                <button
                  onClick={onClose}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Progress Bar */}
            <div className="px-6 py-4">
              <div className="flex items-center">
                <div
                  className={`w-1/2 h-2 rounded-l-full ${
                    step >= 1 ? "bg-blue-600" : "bg-gray-200"
                  }`}
                />
                <div
                  className={`w-1/2 h-2 rounded-r-full ${
                    step >= 2 ? "bg-blue-600" : "bg-gray-200"
                  }`}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="px-6 pb-6">
              {/* General Error Message */}
              {errors.general && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                  <button
                    onClick={resetForm}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Start registration from beginning
                  </button>
                </div>
              )}

              {step === 1 ? (
                // Step 1: Organization Details
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building className="w-4 h-4 inline mr-2" />
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.organization.name}
                      onChange={(e) =>
                        updateOrganization("name", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.organizationName
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="Your Company Name"
                    />
                    {errors.organizationName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.organizationName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      value={formData.organization.industry}
                      onChange={(e) =>
                        updateOrganization("industry", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.industry ? "border-red-300" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Industry</option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                    {errors.industry && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.industry}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Users className="w-4 h-4 inline mr-2" />
                      Company Size *
                    </label>
                    <select
                      value={formData.organization.size}
                      onChange={(e) =>
                        updateOrganization("size", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.size ? "border-red-300" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Company Size</option>
                      {companySizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    {errors.size && (
                      <p className="text-red-500 text-sm mt-1">{errors.size}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.organization.website}
                      onChange={(e) =>
                        updateOrganization("website", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>
              ) : (
                // Step 2: User Details
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.user.firstName}
                        onChange={(e) =>
                          updateUser("firstName", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.firstName
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.user.lastName}
                        onChange={(e) => updateUser("lastName", e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.lastName ? "border-red-300" : "border-gray-300"
                        }`}
                        placeholder="Doe"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.user.email}
                      onChange={(e) => updateUser("email", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="john@company.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Lock className="w-4 h-4 inline mr-2" />
                      Password *
                    </label>
                    <input
                      type="password"
                      value={formData.user.password}
                      onChange={(e) => updateUser("password", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.password ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="Minimum 8 characters"
                    />
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Job Title *
                    </label>
                    <input
                      type="text"
                      value={formData.user.jobTitle}
                      onChange={(e) => updateUser("jobTitle", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.jobTitle ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="Product Manager"
                    />
                    {errors.jobTitle && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.jobTitle}
                      </p>
                    )}
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <User className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">
                          Admin Account
                        </h4>
                        <p className="text-sm text-blue-700 mt-1">
                          As the first user, you'll be the organization admin
                          with full access to manage teams, projects, and
                          settings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={step === 1 ? onClose : handleBack}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isLoading}
              >
                {step === 1 ? "Cancel" : "Back"}
              </button>

              {step === 1 ? (
                <button
                  onClick={handleNext}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    "Create Organization"
                  )}
                </button>
              )}
            </div>

            {/* Switch to Login Footer */}
            <div className="px-6 py-4 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <button
                  onClick={onSwitchToLogin}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  disabled={isLoading}
                >
                  Sign in here
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignupForm;
